import { inject } from "@angular/core";
import { CanActivateChildFn, Router } from "@angular/router";
// import { AccountsService, IAccount } from "@services/accounts.service";
import { AuthenticationService } from "@services/authentication.service";
import { OrganisationDataService } from "@services/organisation-data.service";
import { siteSubscriptions } from "@services/siteSubscriptions";
import { combineLatest, firstValueFrom, take } from "rxjs";

/**
 * AccountOrganisationGuard
 * @param route 
 * @param state 
 * @returns 
 */
export const AccountOrganisationGuard: CanActivateChildFn = async (route, state) => {
    /** Flag to indicate if a new account is being selected */
    let NewAccount: boolean = false;

    /** Inject the Router service to handle navigation */
    const router = inject(Router);

    /** Get the account and organisation UIDs from the route parameters */
    let accountUID: string | null = null;
    let organisationUID: string | null = null;

    /** Iterate through the route path to find the account and organisation UIDs */
    for (const r of route.pathFromRoot) {
        accountUID ??= r.paramMap.get('account');
        organisationUID ??= r.paramMap.get('organisation');
        if (accountUID && organisationUID) { break; }
    }

    /** If either the account or organisation is missing, redirect to unauthorised */
    if (!accountUID || !organisationUID) {
        /* Redirect to unauthorised */
        return redirectUnauthorised(router, state.url, "Account or Organisation parameter is missing in the route");
    }

    try {
        const _authentication = inject(AuthenticationService);
        const _siteSubscriptions = inject(siteSubscriptions);
        // const _AccData = inject(AccountsService);
        const _OrgData = inject(OrganisationDataService);

        /** Get the current user, my accounts, selected account and selected organisation */
        let [currentUser, myAccounts, selectedAccount, selectedOrganisation] = await firstValueFrom(
            combineLatest([
                _authentication.currentUser$.pipe(
                    // filter(a => !!a),
                    take(1)
                ),
                _siteSubscriptions.myAccounts$.pipe(
                    // filter(a => !!a),
                    take(1)
                ),
                _siteSubscriptions.selectedAccount$.pipe(
                    // filter(a => !!a),
                    take(1)
                ),
                _siteSubscriptions.selectedOrganisation$.pipe(
                    // filter(a => !!a),
                    take(1)
                )]
            )
        );

        /* Check if the selected account is different from the account in the route */
        if (selectedAccount.GUID != accountUID) {
            NewAccount = true;

            // /* Need to select correct Account */
            // let data = {
            //     "userPublicID": currentUser?.Sub,
            //     "showDisabled": false
            // };            
            // let myAccounts = <IAccount[]>await firstValueFrom(
            //     _AccData.getAccounts$(data).pipe(
            //         // filter(a => !!a),
            //         take(1)
            //     )
            // )            

            /* Set the myAccounts in siteSubscriptions */
            _siteSubscriptions.setMyAccounts(myAccounts);
            const account = myAccounts.find(a => a.GUID.toLocaleLowerCase() === accountUID.toLocaleLowerCase());
            if (!account) {
                /* Account doesn't exist / user doesn't have access */
                /* Redirect to unauthorised */
                return redirectUnauthorised(router, state.url, "Account doesn't exist or user doesn't have access");
            }

            /* Set the selected account to the one found in myAccounts */
            selectedAccount = account;

        }

        /* Check if the selected organisation is different from the organisation in the route */
        if (selectedAccount && (NewAccount || !selectedOrganisation || selectedOrganisation?.Uid != organisationUID)) {

            /* Need to select correct Organisation */
            let myOrganisations = await firstValueFrom(
                _OrgData.getOrganisations$(selectedAccount.GUID).pipe(
                    // filter(a => !!a),
                    take(1)
                )
            )
            /* Set the myOrganisations in siteSubscriptions */
            _siteSubscriptions.setmyOrganisations(myOrganisations);
            const organisation = myOrganisations.find((o: any) => o.Uid.toLocaleLowerCase() === organisationUID.toLocaleLowerCase());
            if (!organisation) {
                /** Organisation doesn't exist / user doesn't have access */
                /* Redirect to unauthorised */
                return redirectUnauthorised(router, state.url, "Organisation doesn't exist or user doesn't have access");
            }

            /* Set the selected organisation to the one found in myOrganisations */
            selectedOrganisation = organisation;

        } else {
            /* Organisation is already selected and matches the route, no action needed */
            return true;
        }

        /* Set the selected account and organisation in the siteSubscriptions */
        _siteSubscriptions.SetSelectedAccount(selectedAccount, true);
        _siteSubscriptions.SetSelectedOrganisation(selectedOrganisation, true);

    }
    catch (error) {
        /* Handle any errors that occur during the process */
        console.error("Error fetching account or organisation:", error);
        return false;
    }

    /* If everything is successful, allow navigation to the child route */
    return true;

};

/**
 * Redirects to the unauthorised page with the original URL in state
 * @param router Router instance to handle navigation
 * @param url Original URL that the user attempted to access
 * @param message Error Message to the unauthorised page
 * @returns 
 */
const redirectUnauthorised = (router: Router, url: string, message: string = ""): false => {

    /** Redirect to the unauthorised page with the original URL in state */
    router.navigate(
        ['/unauthorised'],
        { state: { 
            url: url, 
            message: message ,
            source: "AccountOrganisationGuard"
        } }
    );

    return false;
};